import {
  EMailingSubcategory,
  EMailingsCategory,
  ETemporaryBlockedTypes,
  EUserCompanyProfileTabs,
} from './definitions';

const subtitle =
  'Your account is temporarily blocked until {{date}}. To activate your account, contact technical support.';
const licenseStatusEmail = 'Activation, renewal, suspension, blocking of license';
const notEnoughFundsLicenseEmail = 'Balance top-up required to pay for license payment';
const registrationTemplateEmail = 'Template registration';
const registrationChannelEmail = 'Channel registration';

export default {
  balance: 'Account balance:',
  overdraft: 'Overdraft',
  blockedByLicense:
    'Any changes to the company profile are currently unavailable. Contact <0>your manager</0> for help.',
  serviceUnavailable: 'Service is temporarily unavailable. Please try again later',
  tabs: {
    [EUserCompanyProfileTabs.PAYMENT]: 'Payment',
    [EUserCompanyProfileTabs.LICENSE]: 'License and pricing',
    [EUserCompanyProfileTabs.REQUISITES]: 'Company details',
    [EUserCompanyProfileTabs.MAILINGS]: 'Notifications',
    [EUserCompanyProfileTabs.USERS]: 'Users',
  },
  messages: {
    haveExpiredLicense_one:
      'You have an unpaid license invoice. The license for the channel {{channels}} has been blocked. To resume work, you need to pay the invoice issued.',
    haveExpiredLicense_other:
      'You have an unpaid license invoice. The license for the channels {{channels}} has been blocked. To resume work, you need to pay the invoices issued.',
    haveExpiredBillForLicense_one:
      'You have an unpaid license invoice. The operation of the channel {{channels}} has been blocked. To resume work, you need to pay the invoice issued.',
    haveExpiredBillForLicense_other:
      'You have an unpaid license invoice. The operation of the channels {{channels}} has been blocked. To resume work, you need to pay the invoices issued.',
    haveNotMoney: "You don't have enough funds to continue using the service.",
    haveNotPayedBills:
      'You have unpaid invoices. In case of non-payment, your work in the system may be limited.',
    addBalance: 'Please top up your balance.',
    lowBalance:
      'There may not be enough available funds to send broadcasts. It might be worth topping up your balance.',
    broadcastBlocked: `Your access to the services is suspended until {{date}}.
       You won't be able to send broadcasts or receive messages until then. Any broadcasts you create will be saved as drafts.
       To activate the services, contact technical support.`,
    accessBlocked: `Your access to the services is suspended until {{date}}.
       Sending and receiving messages will be unavailable until then.
       To activate the services, contact technical support.`,
  },
  buttons: {
    openDocumentList: 'Open document list',
    contactSupport: 'Contact technical support',
  },
  temporaryBlockModal: {
    toProfile: 'To profile',
    [ETemporaryBlockedTypes.BROADCASTS]: {
      title: 'You cannot register broadcasts',
      subtitle,
    },
    [ETemporaryBlockedTypes.TEMPLATES]: {
      title: 'You cannot register templates',
      subtitle,
    },
    [ETemporaryBlockedTypes.CHANNELS]: {
      title: 'You cannot register channels',
      subtitle,
    },
    [ETemporaryBlockedTypes.CASCADES]: {
      title: 'You cannot register cascades',
      subtitle,
    },
    [ETemporaryBlockedTypes.OTHER]: {
      title: 'Your account is temporarily blocked',
      subtitle: 'To activate your account, contact technical support.',
    },
  },
  users: {
    title: 'Account users',
    name: 'Name',
    login: 'Login',
    role: 'Role',
    status: 'Status',
    invitationSent: 'Invitation sent',
    locked: 'Blocked',
    active: 'Active',
    blockSuccess: 'User blocked successfully',
    addSuccess: 'User created successfully',
    generateLink: 'Create login link',
    blockModal: {
      title: 'Block user?',
      description: 'If you block the user, they will lose access to the account.',
      block: 'Block',
    },
    addModal: {
      title: 'Add user',
      name: 'User name',
      login: 'User email',
      role: 'User role',
      add: 'Create login link',
      selectRole: 'Select role',
    },
    generateLinkModal: {
      title: 'Generate a user registration link?',
      description: 'Registration login is the email address of the user',
    },
    viewLinkModal: {
      title: 'User registration link',
      description: 'Copy this link and send it to the user so they could log into edna',
      hint: 'Please remember, the link is valid for 24 hours',
      link: 'Registration link',
      submit: 'Got it',
    },
  },
  mailings: {
    description:
      'Configure email notifications to timely receive information about events in your personal account',
    table: {
      channel: 'Channel',
      mailings: 'Event notifications',
      recipient: 'Recipient',
      hide: 'Collapse',
      showAll: '+ {{count}} more',
    },
    settingsModal: {
      recipients: 'Notification recipients',
      mailing: '<0>Event notification:</0> {{subcategory}}',
      addRecipient: 'Add recipient',
      email: "Recipient's email",
      channel: '<0>Channel:</0> {{channelType}}',
      save: 'Save changes',
      emailAlreadyExist: 'This email address has already been added to the recipient list',
      incorrectEmail: 'Invalid email address',
      repeatCount: 'Number of repetitions',
      repeatDuration: 'Interval between repetitions',
      hasRepeat: 'Send repeated notifications',
      balanceLimit: 'Notify when balance decreases to',
      times_one: 'time',
      times_other: 'times',
    },
    successNotification: 'Notification recipients updated successfully',
    cancelModal: {
      title: 'Save changes?',
      subtitle: 'You have changed the list of notification recipients',
      cancelButton: "Don't save",
    },
    category: {
      [EMailingsCategory.CHANNELS_EMAIL_NOTIFICATIONS]: {
        title: 'Channel notifications',
        hint: 'You can add or remove recipients of notifications about channel events',
        settingsTitle: 'Configure notifications about channel events',
      },
      [EMailingsCategory.TEMPLATES_EMAIL_NOTIFICATIONS]: {
        title: 'Template notifications',
        hint: 'You can add or remove recipients of notifications about template events',
        settingsTitle: 'Configure notifications about template events',
      },
      [EMailingsCategory.LICENSE_EMAIL_NOTIFICATIONS]: {
        title: 'License notifications',
        hint: 'You can add or remove recipients of notifications about license events',
        settingsTitle: 'Configure notifications about license events',
      },
      [EMailingsCategory.BALANCE_EMAIL_NOTIFICATIONS]: {
        title: 'Balance notifications',
        hint: 'You can add or remove recipients of balance notifications',
        settingsTitle: 'Configure balance notifications',
      },
      [EMailingsCategory.PAYKEEPER_EMAIL_NOTIFICATIONS]: {
        title: 'PayKeeper notifications',
        hint: 'You can add or remove recipients of notifications about events related to the PayKeeper payment system',
        settingsTitle:
          'Configure notifications about events related to the PayKeeper payment system',
      },
      [EMailingsCategory.STRIPE_EMAIL_NOTIFICATIONS]: {
        title: 'Stripe auto top-up notifications',
        hint: 'You can add or remove recipients of notifications about events related to the Stripe payment system',
        settingsTitle: 'Configure notifications about events related to the Stripe payment system',
      },
      [EMailingsCategory.TENANT_ACCESS_EMAIL_NOTIFICATIONS]: {
        title: 'Personal account access notifications',
        hint: 'You can add or remove recipients of notifications about personal account access',
        settingsTitle: 'Configure notifications about personal account access',
      },
    },
    subcategory: {
      [EMailingSubcategory.REGISTRATION_OK_NOTIFY_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_VK_NOTIFY_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_VIBER_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_SMS_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_WHATSAPP_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_MAX_BOT_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_MAX_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.REGISTRATION_TG_CHANNEL_EMAIL]: registrationChannelEmail,
      [EMailingSubcategory.RESTRICTIONS_WABA_EMAIL]:
        'Violations, restrictions, or deactivation of WhatsApp Business account',
      [EMailingSubcategory.CHANGE_QUALITY_WHATSAPP_CHANNEL_EMAIL]:
        'Change in channel quality, limit, or status',
      [EMailingSubcategory.REGISTRATION_VK_NOTIFY_TEMPLATE_EMAIL]: registrationTemplateEmail,
      [EMailingSubcategory.REGISTRATION_OK_NOTIFY_TEMPLATE_EMAIL]: registrationTemplateEmail,
      [EMailingSubcategory.REGISTRATION_SMS_TEMPLATE_EMAIL]: registrationTemplateEmail,
      [EMailingSubcategory.REGISTRATION_VIBER_TEMPLATE_EMAIL]: registrationTemplateEmail,
      [EMailingSubcategory.REGISTRATION_WHATSAPP_TEMPLATE_EMAIL]: registrationTemplateEmail,
      [EMailingSubcategory.CHANGE_WHATSAPP_TEMPLATE_EMAIL]: 'Template category change',
      [EMailingSubcategory.DISABLE_WHATSAPP_TEMPLATE_EMAIL]:
        'Template deactivation and termination',
      [EMailingSubcategory.SMS_LICENSE_STATUS_EMAIL]: licenseStatusEmail,
      [EMailingSubcategory.NOT_ENOUGH_FUNDS_FOR_SMS_LICENSE_EMAIL]: notEnoughFundsLicenseEmail,
      [EMailingSubcategory.VIBER_LICENSE_STATUS_EMAIL]: licenseStatusEmail,
      [EMailingSubcategory.NOT_ENOUGH_FUNDS_FOR_VIBER_LICENSE_EMAIL]: notEnoughFundsLicenseEmail,
      [EMailingSubcategory.WHATSAPP_LICENSE_STATUS_EMAIL]: licenseStatusEmail,
      [EMailingSubcategory.NOT_ENOUGH_FUNDS_FOR_WHATSAPP_LICENSE_EMAIL]: notEnoughFundsLicenseEmail,
      [EMailingSubcategory.MAX_BOT_LICENSE_STATUS_EMAIL]: licenseStatusEmail,
      [EMailingSubcategory.NOT_ENOUGH_FUNDS_FOR_MAX_BOT_LICENSE_EMAIL]: notEnoughFundsLicenseEmail,
      [EMailingSubcategory.MAX_LICENSE_STATUS_EMAIL]: licenseStatusEmail,
      [EMailingSubcategory.TG_LICENSE_STATUS_EMAIL]: licenseStatusEmail,
      [EMailingSubcategory.NOT_ENOUGH_FUNDS_FOR_MAX_LICENSE_EMAIL]: notEnoughFundsLicenseEmail,
      [EMailingSubcategory.NOT_ENOUGH_FUNDS_FOR_TG_LICENSE_EMAIL]: notEnoughFundsLicenseEmail,
      [EMailingSubcategory.PAYKEEPER_PAYMENT_EMAIL]: 'Card payment',
      [EMailingSubcategory.STRIPE_TOP_UP_BALANCE_EMAIL]:
        'Balance auto top-up via Stripe payment system',
      [EMailingSubcategory.OVERDRAFT_STATUS_EMAIL]: 'Overdraft limit change notification',
      [EMailingSubcategory.BALANCE_DROPPED_BELOW_THRESHOLD_EMAIL]:
        'Minimum balance threshold reached',
      [EMailingSubcategory.RICH_BALANCE_LIMIT_EMAIL]: 'Payment reminder',
      [EMailingSubcategory.TENANT_ACCESS_EMAIL]: 'Access to personal account',
    },
  },
};
